import { memo, useId, useMemo, useState } from 'react';
import { Badge } from '../../../atoms/Badge/Badge';
import { Button } from '../../../atoms/Button/Button';
import { Icon } from '../../../atoms/Icon/Icon';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { Search } from '../../../molecules/Search/Search';
import { Select, type SelectOption } from '../../../molecules/Select/Select';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import type { TableHeaderSort } from '../../../molecules/TableHeaderLabel/TableHeaderLabel';
import { Table, type TableColumn, type TableRowData } from '../../../organisms/Table/Table';
import { Dialog } from '../../../primitives/Dialog/Dialog';
import { SENTIMENT_BADGE, pageWindow, wholeFormat as whole } from '../../tables/shared/tableShared';
import styles from './TuneSentimentModal.module.css';

/**
 * Competitors — `Modal` at `Type=Tune sentiment` (2037:9142), opened by the
 * `Tune sentiment` button on Top performing content.
 *
 * The same posts as that table, laid out for correcting what the classifier
 * called them. Each post shows its **detected** sentiment and a select for its
 * **actual** one; `Save changes` hands the corrections to the caller, which
 * saves them — from then on they replace the detected sentiment everywhere.
 *
 * **Not the `Modal` component.** This is a whole `Table` on the `Overlay`, with
 * no modal header or actions of its own, so it composes `Dialog` around a
 * `Table` and gets the focus trap, Escape, overlay click, focus return and
 * scroll lock for free.
 *
 * **Bulk editing went on 2026-09-25** (the frame's `Tables Filters Bar` is now
 * `visible=false`), and with it the row selection and the View filter.
 * Correcting sentiment is a per-post judgement; search still narrows the list.
 *
 * Decisions the frame does not make, most settled with the user 2026-09-18:
 *
 *  1. **Edits are local until saved.** `Cancel`, Escape and an overlay click
 *     all discard them silently.
 *  2. **A post can be confirmed at its detected sentiment** — recorded as
 *     reviewed, changing no figure.
 *  3. **The actual value is *not* coloured by sentiment** — the select's plain
 *     `text/primary` value; the `Detected sentiment` badge is where sentiment
 *     is coloured.
 */

const PAGE_SIZE = 10;

/** One post, as the modal lists it. */
export type TunePostView = {
  id: number;
  /** Position by interactions, 1-based. */
  rank: number;
  /** `Robi · Network coverage complaint on Facebook`. */
  summary: string;
  interactions: number;
  detected: string;
  detectedLabel: string;
  /** The saved correction, or null — what the Actual select shows before any edit. */
  corrected: string | null;
};

export type TuneSentimentModalProps = {
  open: boolean;
  onClose: () => void;
  /** The posts Top performing content draws — the same list, highest interactions first. */
  posts: readonly TunePostView[];
  /** The three sentiments, in the chips' own order. Plain options (not coloured). */
  sentimentOptions: SelectOption[];
  /** `Save changes` with at least one edit: post id → chosen sentiment. The modal closes itself after. */
  onSave: (changes: Record<number, string>) => void;
  /** Figma's `View ↗` on each row. A no-op until there is a destination. */
  onViewPost?: (post: TunePostView) => void;
};

export function TuneSentimentModal({
  open,
  onClose,
  posts,
  sentimentOptions,
  onSave,
  onViewPost,
}: TuneSentimentModalProps) {
  const titleId = useId();

  /*
   * A fresh editing session every time it opens: unsaved edits, the search and
   * the page all start clean. Bumped during render — React's pattern for state
   * derived from a prop — rather than in an effect, which would paint the
   * previous session's edits for a frame first.
   */
  const [session, setSession] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSession((n) => n + 1);
  }

  return (
    <Dialog open={open} onClose={onClose} labelledBy={titleId} className={styles.layer}>
      <Body
        key={session}
        posts={posts}
        sentimentOptions={sentimentOptions}
        titleId={titleId}
        onClose={onClose}
        onSave={onSave}
        onViewPost={onViewPost}
      />
    </Dialog>
  );
}

const Body = memo(function Body({
  posts,
  sentimentOptions,
  titleId,
  onClose,
  onSave,
  onViewPost,
}: {
  posts: readonly TunePostView[];
  sentimentOptions: SelectOption[];
  titleId: string;
  onClose: () => void;
  onSave: (changes: Record<number, string>) => void;
  onViewPost?: (post: TunePostView) => void;
}) {
  /** Unsaved edits, by post. Only `Save changes` hands these over. */
  const [pending, setPending] = useState<Record<number, string>>({});
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  /* Only `Engagement` draws the sort chevron. Opens highest first. */
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered =
      q === '' ? posts : posts.filter((post) => post.summary.toLowerCase().includes(q));
    /* The list is already highest-first; ascending is the same list reversed. */
    return direction === 'desc' ? filtered : [...filtered].reverse();
  }, [posts, query, direction]);

  const pageCount = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visible = shown.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  /** What the Actual select shows: this session's edit, else the saved one, else nothing. */
  const actualOf = (post: TunePostView): string | undefined =>
    pending[post.id] ?? post.corrected ?? undefined;

  const setActual = (id: number, sentiment: string) =>
    setPending((prev) => ({ ...prev, [id]: sentiment }));

  const save = () => {
    if (Object.keys(pending).length > 0) onSave(pending);
    onClose();
  };

  const engagementSort: TableHeaderSort = direction;

  /*
   * The frame's widths as a share of its 1192 — re-cut 2026-09-25 with the
   * filters bar: 588 + 113 + 134 + 280 + 77 = 1192.
   */
  const columns: TableColumn[] = [
    { key: 'summary', header: 'Post summary', width: '49.33%' },
    {
      /* Figma renamed this from `No of interactions` on 2026-09-25. */
      key: 'engagement',
      header: 'Engagement',
      width: '9.48%',
      sort: engagementSort,
      onSort: () => {
        setDirection((d) => (d === 'desc' ? 'asc' : 'desc'));
        setPage(1);
      },
    },
    { key: 'detected', header: 'Detected sentiment', cellType: 'badge', width: '11.24%' },
    { key: 'actual', header: 'Actual sentiment', cellType: 'selectDropdown', width: '23.49%' },
    /* Figma `Text=False` — a header cell with no label over the row links. */
    { key: 'view', cellType: 'actionButtons', width: '6.46%' },
  ];

  const tableRows: TableRowData[] = visible.map((post) => ({
    key: String(post.id),
    cells: {
      summary: { text: post.summary },
      engagement: { text: whole.format(post.interactions) },
      detected: {
        content: (
          <Badge type={SENTIMENT_BADGE[post.detected] ?? 'gray'}>{post.detectedLabel}</Badge>
        ),
      },
      actual: {
        content: (
          <Select
            className={styles.select}
            aria-label={`Actual sentiment of post #${post.rank}`}
            options={sentimentOptions}
            value={actualOf(post)}
            onValueChange={(next) => setActual(post.id, next)}
            placeholder="Change to..."
            /* The last rows would open into the table's clipped edge — see `placement`. */
            placement="auto"
          />
        ),
      },
      view: {
        content: (
          <Button
            hierarchy="ghostColor"
            iconTrailing={<Icon name="arrowUpRight" />}
            onClick={() => onViewPost?.(post)}
            aria-label={`View post #${post.rank}`}
          >
            View
          </Button>
        ),
      },
    },
  }));

  const count = posts.length;

  return (
    <Table
      elevated
      className={styles.panel}
      label="Tune sentiment"
      columns={columns}
      rows={tableRows}
      emptyLabel={query.trim() !== '' ? 'No posts match that search' : 'No posts in this period'}
      header={
        <TableHeader
          /* The table's own title names the dialog — see `labelledBy`. */
          title="Top performing content"
          titleId={titleId}
          badge={`${whole.format(count)} total ${count === 1 ? 'post' : 'posts'}`}
          helpText="Correct the sentiment the classifier detected. Saved corrections replace it everywhere on the dashboard for the rest of this session."
          helpSupportingText="Nothing changes until Save changes. Cancel, Escape or clicking outside discards every edit made since the modal opened."
          search={
            <Search
              aria-label="Search content"
              placeholder="Search content..."
              value={query}
              onValueChange={(next) => {
                setQuery(next);
                setPage(1);
              }}
              /* Where the dialog lands — not the title's help icon, whose tooltip would open every time. */
              // eslint-disable-next-line jsx-a11y/no-autofocus -- writes native `autofocus` for `<dialog>`; see Search and docs/design/rules.md §1.
              autoFocus
            />
          }
          actions={
            <>
              <Button hierarchy="tertiary" onClick={onClose}>
                Cancel
              </Button>
              <Button hierarchy="primary" onClick={save}>
                Save changes
              </Button>
            </>
          }
        />
      }
      pagination={
        <Pagination
          page={currentPage}
          pageCount={pageCount}
          pages={pageWindow(currentPage, pageCount)}
          onPageChange={setPage}
          label="Tune sentiment pages"
        />
      }
    />
  );
});
